import { BreakpointObserver } from '@angular/cdk/layout';
import { formatDate } from '@angular/common';
import { Component, HostListener, inject, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { CodaltModusService } from 'src/app/services/codalt-modus.service';
import { LocationService } from 'src/app/services/location.service';
import { environment } from '../../../../environments/environment';
import { entityTypes } from '../../../../environments/trackline/entitytypes';
import { Entity } from '../../../classes/entity.class';
import { PlanningRepetition } from '../../../classes/planning-repetition';
import { Planning } from '../../../classes/planning.class';
import { User } from '../../../classes/user.class';
import { CodaltComponent } from '../../../codalt.component';
import { DIALOG_CLOSE } from '../../../components/field-input/fields/dialogCloseToken';
import { FieldTemplate, getTemplateForEntityType } from '../../../components/fields-template/field-template';
import { AuthorisationService } from '../../../services/auth/authorisation.service';
import { Features } from '../../../services/auth/features';
import { ConfirmDialogService } from '../../../services/confirm-dialog-service/confirm-dialog.service';
import { DialogEscapeService } from '../../../services/dialog/dialog-escape.service';
import { EntitiesService } from '../../../services/entities/entities.service';
import { EntityType } from '../../../services/entities/entity-type.class';
import { EntityTypeService, PlanningFormData } from '../../../services/entity-type.service';
import { PlanningFixedService } from '../../../services/planning/planning-fixed.service';
import { PlanningService } from '../../../services/planning/planning.service';
import { LocalStorage } from '../../../storage.class';
import { Utils } from '../../../utils.class';
import { PlanningHighlightService } from '../trackline-planning/planning-highlight.service';
import { TracklinePlanningFilesComponent } from './trackline-planning-files/trackline-planning-files.component';

const WEEKDAY_KEYS = [
    'repeat_monday',
    'repeat_tuesday',
    'repeat_wednesday',
    'repeat_thursday',
    'repeat_friday',
    'repeat_saturday',
    'repeat_sunday',
] as const;

enum RepeatOptions {
    OnlyThis = 'OnlyThis',
    ThisAndFollowing = 'ThisAndFollowing',
}

const NO_EMIT = { emitEvent: false } as const;

@Component({
    selector: 'app-trackline-planning-edit-dialog',
    standalone: false,
    templateUrl: './trackline-planning-edit-dialog.component.html',
    styleUrl: './trackline-planning-edit-dialog.component.scss',
    providers: [
        {
            provide: DIALOG_CLOSE,
            useFactory: (ref: MatDialogRef<any>) => ref.beforeClosed(),
            deps: [MatDialogRef],
        },
    ],
})
export class TracklinePlanningEditDialogComponent extends CodaltComponent implements OnInit, OnDestroy {
    private _saving = false;

    get activeTemplate(): FieldTemplate {
        return getTemplateForEntityType(this.mainEntityType?.key);
    }

    get saving(): boolean {
        return this._saving;
    }

    set saving(value: boolean) {
        this._saving = value;
        this.dialogEscapeService.setCloseBlocked(this.dialogRef, value);
    }

    fieldFormData: PlanningFormData;
    mainEntityType: EntityType;
    externalChanged = false;
    deleted = false;
    fixedDate: Date;
    existingPlannings: { id: number; begindate: string; entitytype_key: string }[] = [];
    entities$ = this.entitiesService.getByType(environment.planningEntityTypes.map(e => e.key));
    entityTypesTable: EntityType[];
    inPast: boolean = false;
    isExistingPlanning = false;
    mobile = false;
    dragOver = false;
    @ViewChild(TracklinePlanningFilesComponent) planningFilesComponent: TracklinePlanningFilesComponent;
    private timers: ReturnType<typeof setTimeout>[] = [];
    protected codaltModusService = inject(CodaltModusService);
    private planningHighlightService = inject(PlanningHighlightService);

    constructor(
        public entityTypeService: EntityTypeService,
        private entitiesService: EntitiesService,
        private planningService: PlanningService,
        private planningFixedService: PlanningFixedService,
        private confirmDialogService: ConfirmDialogService,
        private dialogEscapeService: DialogEscapeService,
        private dialog: MatDialog,
        private router: Router,
        private breakpointObserver: BreakpointObserver,
        public dialogRef: MatDialogRef<TracklinePlanningEditDialogComponent>,
        private locationService: LocationService,

        @Inject(MAT_DIALOG_DATA)
        public data: {
            copy: boolean;
            move: boolean;
            planning: Planning;
            beginDate: Date;
            endDate: Date;
            statusKey: string;
            mainEntity: Entity;
            otherEntities: Entity[];
            foreman: User;
            entityTypeKey?: string;
            dirty?: boolean;
            infoMessage?: string;
        }
    ) {
        super();

        this.isExistingPlanning = !!this.data?.planning?.id;
    }

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent) {
        if (!this.fieldFormData || this.fieldFormData.form.disabled) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = true;
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
        if (!this.fieldFormData || this.fieldFormData.form.disabled || !this.planningFilesComponent) {
            return;
        }
        const file = event.dataTransfer?.files?.[0];
        if (file) {
            this.planningFilesComponent.uploadDroppedFile(file);
        }
    }

    ngOnInit() {
        this.subscriptions.add(
            this.breakpointObserver.observe('(max-width: 722px)').subscribe(match => {
                this.mobile = match.matches;
            })
        );

        if (this.isExistingPlanning) {
            this.setupExistingPlanning();
        } else {
            this.createNewPlanning();
        }

        this.setupPastIndicator();
    }

    isNewRepetition(
        nrOfRepetitions: number = this.fieldFormData?.form?.controls?.planning?.controls?.nrOfRepetitions?.value,
        repetitionId: number = this.fieldFormData?.orginal?.planning_repetition_id
    ) {
        if (!this.isExistingPlanning) {
            return true;
        }

        return nrOfRepetitions > 1 && this.existingPlannings.length === 0 && !repetitionId;
    }

    setupExistingPlanning() {
        this.subscriptions.add(
            combineLatest([
                this.planningService.getSingle(this.data?.planning.id, !!this.data?.planning?.deleted_at),
                this.planningFixedService.getLatestItem(),
            ]).subscribe(([planning, fixed]) => {
                const { move, copy, entityTypeKey } = this.data;

                if (planning.deleted_at) {
                    this.deleted = true;

                    this.externalChanged = true;

                    this.externalChangeDisable();

                    return;
                }

                if (move) {
                    planning = this.data.planning;
                }

                const isFixed =
                    planning.mainPlanning.begindate !== null &&
                    new Date(fixed.fixeduntil).getTime() >= new Date(planning.mainPlanning.begindate).getTime();

                this.fixedDate = isFixed ? new Date(fixed.fixeduntil) : null;

                if (this.fieldFormData) {
                    this.externalChanged = hasExternalChanges(planning, this.fieldFormData.orginal);

                    if (this.externalChanged) {
                        this.externalChangeDisable();
                    }
                } else {
                    this.mainEntityType = this.entitytypes[entityTypeKey ?? planning.mainPlanning.entitytype_key];

                    this.setTableEntityTypes();

                    this.fieldFormData = this.entityTypeService.getFormFromPlanning(
                        planning,
                        this.subscriptions,
                        copy,
                        entityTypeKey
                    );

                    this.setFormValuesFromData();

                    if (planning?.planning_repetition?.id) {
                        this.getExistingPlanningsForRepetition(planning);
                    } else {
                        this.setupRepetition();
                    }

                    this.triggerValidation();
                }
            })
        );
    }

    externalChangeDisable() {
        this.fieldFormData.form.disable(NO_EMIT);
        this.fieldFormData.form.controls.mainPlanningHas.controls.phe.get('begindate').disable();
        this.fieldFormData.form.controls.mainPlanningHas.controls.phe.get('enddate').disable();

        ['repetitionEndDate', 'interval_week', 'nrOfRepetitions', 'repetition', 'weekdays'].forEach(field => {
            this.fieldFormData.form.controls.planning.get(field).disable(NO_EMIT);
        });
    }

    triggerValidation() {
        this.timers.push(
            setTimeout(() => {
                Utils.triggerValidationP(this.fieldFormData.form);
            }, 500)
        );
    }

    createNewPlanning() {
        const { mainEntity, otherEntities } = this.data;
        this.mainEntityType = entityTypes[mainEntity.entitytypes[0].key];
        this.fieldFormData = this.entityTypeService.getFormFromEntityType(
            this.mainEntityType,
            mainEntity,
            this.subscriptions
        );

        (otherEntities ?? []).forEach(relatedEntity => {
            const relatedEntityTypeKey = relatedEntity.entitytypes[0]?.key;

            if (relatedEntityTypeKey) {
                const phe = this.entityTypeService.addPheToForm(this.fieldFormData, entityTypes[relatedEntityTypeKey]);
                // timeout so that valueChange on entity Input is triggered
                setTimeout(() =>
                    (phe.form.controls.phe.get('entity_id') as any as FormControl).setValue(relatedEntity.id)
                );
            }
        });

        this.setTableEntityTypes();
        this.setFormValuesFromData();
        this.setupRepetition();
        this.triggerValidation();
    }

    setupPastIndicator() {
        if (this.fieldFormData?.mainPhe) {
            this.subscriptions.add(
                this.fieldFormData.mainPhe.phe.get('begindate').fc.valueChanges.subscribe(value => {
                    this.inPast = value && Utils.isBeforeToday(value);

                    if (
                        this.inPast &&
                        !this.fieldFormData?.orginal?.planning_repetition_id &&
                        AuthorisationService.hasFeature(Features.PlanningSaveSave)
                    ) {
                        this.fieldFormData.planningFieldsMap.get('planning').get('repetition').name =
                            'Herhalen (niet mogelijk in het verleden)';
                    }
                })
            );
        }
    }

    setupRepetition() {
        const { repetition, repetitionEndDate, interval_week, weekdays, nrOfRepetitions } =
            this.fieldFormData.form.controls.planning.controls;

        const begindate = this.fieldFormData.mainPhe.phe.get('begindate').fc;

        const minNrOfRepetitionsValue =
            this.existingPlannings.findIndex(p => Utils.matchesDate(new Date(p.begindate), begindate.value)) + 1;

        if (minNrOfRepetitionsValue > 2) {
            // because two is the default anyways (nothing repeats 1 time)
            nrOfRepetitions.addValidators(Validators.min(minNrOfRepetitionsValue));
        }

        const minRepetitionEndDate = begindate.value ? new Date(begindate.value) : null;

        if (minRepetitionEndDate) {
            minRepetitionEndDate.setDate(minRepetitionEndDate.getDate() + 2); // same here, cannot repeat in a single day
        }

        this.fieldFormData.planningFieldsMap.get('planning').get('repetitionEndDate').validators.minDate =
            minRepetitionEndDate;

        this.subscriptions.add(
            combineLatest([
                repetition.valueChanges.pipe(startWith(repetition.value)),
                weekdays.valueChanges.pipe(startWith(weekdays.value)),
                interval_week.valueChanges.pipe(startWith(interval_week.value)),
                begindate.valueChanges.pipe(startWith(begindate.value)),
                nrOfRepetitions.valueChanges.pipe(startWith(nrOfRepetitions.value)),
                repetitionEndDate.valueChanges.pipe(startWith(repetitionEndDate.value)),
            ]).subscribe(
                ([
                    repetitionValue,
                    weekdaysValue,
                    interval_weekValue,
                    begindateValue,
                    nrOfRepetitionsValue,
                    repetitionEndDateValue,
                ]) => {
                    this.inPast = begindateValue && Utils.isBeforeToday(begindateValue);

                    if (repetitionValue) {
                        this.updateRepetitionForm(
                            weekdaysValue,
                            interval_weekValue,
                            new Date(begindateValue),
                            nrOfRepetitionsValue,
                            repetitionEndDateValue
                        );

                        return;
                    }

                    this.disableRepetitionForm();
                }
            )
        );
    }

    updateRepetitionForm(
        weekdaysValue: string[],
        interval_weekValue: number,
        begindateValue: Date,
        nrOfRepetitionsValue: number,
        repetitionEndDateValue: Date
    ) {
        const isLast = isLastPlanningInRepetition(
            this.existingPlannings,
            this.fieldFormData?.orginal?.mainPlanning?.begindate ?? begindateValue
        );

        if (this.inPast) {
            this.disableRepetitionForm();

            return;
        }

        if (isLast || !AuthorisationService.hasFeature(Features.PlanningSaveSave)) {
            this.updateDatesLabel(nrOfRepetitionsValue, interval_weekValue, weekdaysValue, begindateValue);

            return;
        }

        const { interval_week, weekdays, nrOfRepetitions, repetitionEndDate } =
            this.fieldFormData.form.controls.planning.controls;

        const currentWeekday = `repeat_${formatDate(begindateValue, 'EEEE', 'en').toLowerCase()}`;

        if (repetitionEndDate.disabled && nrOfRepetitionsValue === null) {
            repetitionEndDate.setValue(null, NO_EMIT);

            repetitionEndDate.enable(NO_EMIT);
        } else if (nrOfRepetitionsValue !== null) {
            repetitionEndDate.setValue(null, NO_EMIT);

            repetitionEndDate.disable(NO_EMIT);
        }

        if (nrOfRepetitions.disabled && repetitionEndDateValue === null) {
            nrOfRepetitions.enable(NO_EMIT);
        } else if (repetitionEndDateValue !== null) {
            nrOfRepetitionsValue = calculateNrOfRepetitionsFromEndDate(
                repetitionEndDateValue,
                begindateValue,
                interval_weekValue,
                weekdaysValue
            );

            nrOfRepetitions.setValue(nrOfRepetitionsValue, NO_EMIT);

            nrOfRepetitions.disable(NO_EMIT);
        }

        if (interval_week.disabled) {
            interval_week.enable(NO_EMIT);

            interval_week.setValue(1, NO_EMIT);
        }

        if (weekdays.disabled) {
            weekdays.enable(NO_EMIT);
        }

        if (!weekdaysValue || weekdaysValue.length === 0) {
            weekdays.setValue([`repeat_${formatDate(begindateValue, 'EEEE', 'en').toLowerCase()}`]);

            return;
        } else if (!weekdaysValue.includes(currentWeekday)) {
            weekdays.setValue(
                [...new Set([...weekdaysValue, currentWeekday])].sort(
                    (a, b) =>
                        WEEKDAY_KEYS.indexOf(a as (typeof WEEKDAY_KEYS)[number]) -
                        WEEKDAY_KEYS.indexOf(b as (typeof WEEKDAY_KEYS)[number])
                )
            );

            return;
        }

        this.updateDatesLabel(nrOfRepetitionsValue, interval_weekValue, weekdaysValue, begindateValue);
    }

    updateDatesLabel(
        nrOfRepetitionsValue: number,
        interval_weekValue: number,
        weekdaysValue: string[],
        begindateValue: Date
    ) {
        const firstExistingPlanning = this.existingPlannings.length > 0 ? this.existingPlannings[0] : null;

        let nrOfRepetitions = this.existingPlannings.length > 1 ? this.existingPlannings.length : nrOfRepetitionsValue;

        if (
            nrOfRepetitionsValue !== this.existingPlannings.length &&
            this.existingPlannings.length > 0 &&
            this.fieldFormData.form.controls.planning.controls.nrOfRepetitions.dirty
        ) {
            nrOfRepetitions = nrOfRepetitionsValue;
        }

        const firstPlanningDate = firstExistingPlanning ? new Date(firstExistingPlanning.begindate) : begindateValue;

        this.setDatesLabel(firstPlanningDate, nrOfRepetitions, interval_weekValue, weekdaysValue, begindateValue);
    }

    disableRepetitionForm() {
        const { interval_week, weekdays, nrOfRepetitions, repetitionEndDate, repetition } =
            this.fieldFormData.form.controls.planning.controls;

        if (repetition.enabled && this.inPast) {
            if (!this.isExistingPlanning) {
                repetition.setValue(false, NO_EMIT);
            }

            repetition.disable(NO_EMIT);
        }

        if (repetitionEndDate.enabled) {
            repetitionEndDate.disable(NO_EMIT);
        }

        if (nrOfRepetitions.enabled) {
            nrOfRepetitions.disable(NO_EMIT);
        }

        if (interval_week.enabled) {
            interval_week.disable(NO_EMIT);
        }

        if (weekdays.enabled) {
            if (
                this.isExistingPlanning === true &&
                this.isNewRepetition() === false &&
                this.existingPlannings.length === 0
            ) {
                weekdays.setValue(null, NO_EMIT);
            }

            weekdays.disable(NO_EMIT);
        }

        this.setDatesLabel();
    }

    setDatesLabel(
        begindate?: Readonly<Date>,
        nrOfRepetitions?: Readonly<number>,
        interval_week?: Readonly<number>,
        weekdays?: ReadonlyArray<string>,
        current: Readonly<Date> = new Date()
    ) {
        if (begindate && nrOfRepetitions && interval_week && weekdays) {
            const dates = generateDates(begindate, nrOfRepetitions, interval_week, weekdays);

            this.fieldFormData.form.controls.planning.controls.datesLabel.setValue({
                current,
                dates,
            });
        }
    }

    getExistingPlanningsForRepetition(planning: Readonly<Planning>) {
        this.subscriptions.add(
            this.planningService.getPlanningsByRepetition(planning.planning_repetition.id).subscribe({
                next: existingPlannings => {
                    if (!this.data.copy) {
                        this.existingPlannings = existingPlannings;
                    }

                    const nrOfExistingPlannings = existingPlannings.length;

                    const { repetition, interval_week, weekdays, nrOfRepetitions } =
                        this.fieldFormData.form.controls.planning.controls;

                    repetition.setValue(nrOfExistingPlannings > 0, NO_EMIT);

                    nrOfRepetitions.setValue(nrOfExistingPlannings > 0 ? nrOfExistingPlannings : null, NO_EMIT);

                    interval_week.setValue(planning.planning_repetition.interval_week, NO_EMIT);

                    weekdays.setValue(WEEKDAY_KEYS.filter(k => planning.planning_repetition[k], NO_EMIT));

                    const startDate = new Date(existingPlannings[0].begindate);

                    this.setDatesLabel(
                        startDate,
                        existingPlannings.length,
                        planning.planning_repetition.interval_week,
                        WEEKDAY_KEYS.filter(k => planning.planning_repetition[k]),
                        startDate
                    );

                    this.setupRepetition();

                    const begindate = this.fieldFormData.mainPhe.phe.get('begindate').fc;

                    const isLast = isLastPlanningInRepetition(
                        existingPlannings,
                        this.fieldFormData?.orginal?.mainPlanning?.begindate ?? begindate.value
                    );

                    if (isLast) {
                        this.disableRepetitionForm();

                        repetition.disable(NO_EMIT);
                    }
                },
                error: error => {
                    console.error('Error fetching existing plannings:', error);
                },
            })
        );
    }

    setFormValuesFromData() {
        const { beginDate, endDate, statusKey, foreman } = this.data;

        if (beginDate) {
            this.fieldFormData.pheFields.forEach(pheField => {
                pheField.form.controls.phe.get('begindate').setValue(beginDate);
                pheField.form.controls.phe.get('enddate').setValue(endDate);
            });
        }

        const planning = this.fieldFormData.form.controls.planning;

        if (!this.isExistingPlanning) {
            planning
                .get('weekdays')
                .setValue([
                    'repeat_monday',
                    'repeat_tuesday',
                    'repeat_wednesday',
                    'repeat_thursday',
                    'repeat_friday',
                    'repeat_saturday',
                    'repeat_sunday',
                ]);
        }

        if (statusKey) {
            planning.get('status_key').setValue(statusKey);
        }

        if (foreman?.id) {
            planning.get('performer').setValue(foreman?.id);
        }

        if (AuthorisationService.hasRole(environment.projectmanager) && !this.isExistingPlanning) {
            planning.get('projectmanager').setValue(LocalStorage.user.id);
        }

        this.subscriptions.add(
            this.fieldFormData.mainPhe.form.controls.phe.get('begindate').valueChanges.subscribe(() => {
                const endDate = this.fieldFormData.mainPhe.form.controls.phe.get('enddate');
                endDate.setValue(endDate.value);
            })
        );
    }

    setTableEntityTypes() {
        this.entityTypesTable = this.mainEntityType.has_entitytype_keys.map(key => entityTypes[key]);
    }

    save() {
        this.saving = true;

        const nrOfRepetitionsControl = this.fieldFormData.form.controls.planning.controls.nrOfRepetitions;
        const repetitionEndDateControl = this.fieldFormData.form.controls.planning.controls.repetitionEndDate;

        if (nrOfRepetitionsControl.value !== null) {
            repetitionEndDateControl.disable(NO_EMIT);
            nrOfRepetitionsControl.enable(NO_EMIT);
        }

        const nrOfRepetitions = nrOfRepetitionsControl.value;

        return Utils.triggerValidationP(this.fieldFormData.form, [], true).then(
            () => {
                const companyAddress = this.locationService.formatAddress(this.environment.companyAddress);

                this.fieldFormData.pheFields.forEach(pheField => {
                    if (pheField.form.value.phe._isMainPlanning) {
                        return;
                    }

                    pheField.form.value.phe.transport_origin = companyAddress;
                    pheField.form.value.phe.transport_destination = companyAddress;
                });

                const planning = this.entityTypeService.getPlanningObject(this.fieldFormData) as any;

                const { interval_week, weekdays, repetition } = planning;

                const weekdayFlags = WEEKDAY_KEYS.reduce(
                    (acc, wd) => {
                        acc[wd] = Array.isArray(weekdays) ? weekdays.includes(wd) : false;
                        return acc;
                    },
                    {} as Record<(typeof WEEKDAY_KEYS)[number], boolean>
                );

                const nextPlanning =
                    this.existingPlannings.length > 0
                        ? this.existingPlannings[this.existingPlannings.findIndex(p => p.id === planning.id) + 1]
                        : null;

                delete planning.nrOfRepetitions;
                delete planning.repetition;
                delete planning.weekdays;
                delete planning.datesLabel;
                delete planning.interval_week;

                const isLast = isLastPlanningInRepetition(
                    this.existingPlannings,
                    planning.mainPlanning?.begindate ??
                        this.fieldFormData.mainPhe.form.controls.phe.get('begindate').value
                );

                if (repetition && nrOfRepetitions > 1 && !isLast) {
                    const current = this.existingPlannings.length;
                    const desired = nrOfRepetitions;
                    const delta = desired - current;
                    const remaining = remainingPlanningsInRepetition(
                        this.existingPlannings,
                        this.fieldFormData.form?.value?.mainPlanningHas.phe?.begindate
                    );
                    const amount = delta >= 0 ? remaining + Math.abs(delta) : remaining - Math.abs(delta);

                    if (this.isNewRepetition()) {
                        return this.saveRepetition(planning, desired, interval_week, weekdayFlags);
                    } else if (desired === current) {
                        return this.saveRepetition(planning, amount, interval_week, weekdayFlags);
                    } else {
                        return this.saveRepetition(planning, amount, interval_week, weekdayFlags);
                    }
                } else if (!repetition && nextPlanning && !this.inPast) {
                    return this.cancelRepetition(planning, nextPlanning);
                } else {
                    return this.savePlanning(planning);
                }
            },
            e => {
                this.saving = false;

                this.confirmDialogService.confirm({
                    title: 'Er is iets misgegaan bij het opslaan van de planning',
                    text: e !== undefined ? JSON.parse(e) : 'Validatie fouten, zie de rood gemarkeerde velden',
                    confirm: 'Oke',
                });

                if (e) {
                    console.error(e);
                }

                return false;
            }
        );
    }

    private savePlanning(planning: Readonly<Planning>) {
        return this.planningService
            .save(planning)
            .toPromise()
            .then(
                savedPlanning => {
                    this.close(savedPlanning.data.planning);

                    return true;
                },
                e => {
                    this.saving = false;

                    this.confirmDialogService.confirm({
                        title: 'Er is iets misgegaan bij het opslaan van de planning',
                        text:
                            e !== undefined && e.message
                                ? e.message
                                : 'Validatie fouten, zie de rood gemarkeerde velden',
                        confirm: 'Oke',
                    });

                    console.error(e);

                    return false;
                }
            );
    }

    private saveRepetition(
        planning_template: Planning,
        nr_of_repetitions: number,
        interval_week: number,
        weekdayFlags: Record<(typeof WEEKDAY_KEYS)[number], boolean>
    ) {
        const { begindate, enddate } = this.fieldFormData.form?.value?.mainPlanningHas.phe;
        const { copy, planning } = this.data;

        const save = (id?: number) => {
            const updated_at = copy && !id ? null : planning_template?.planning_repetition?.updated_at;
            const previous_planning_repetition_id = (copy && planning?.planning_repetition_id) ?? null;

            if (planning) delete planning.planning_repetition;

            delete planning_template.planning_repetition;
            delete planning_template.planning_repetition_id;
            delete planning_template.mainPlanning;
            delete planning_template.planning_has[0].entity;

            return this.planningService
                .saveRepetition(
                    {
                        id: id,
                        begindate,
                        enddate,
                        interval_week,
                        ...weekdayFlags,
                        planning_template,
                        nr_of_repetitions,
                        previous_planning_repetition_id,
                        updated_at,
                    } as unknown as PlanningRepetition,
                    id
                )
                .toPromise()
                .then(
                    savedPlanning => {
                        this.close(savedPlanning.data);

                        return true;
                    },
                    e => {
                        this.saving = false;

                        this.confirmDialogService.confirm({
                            title: 'Er is iets misgegaan bij het opslaan van de planning',
                            text:
                                e.status === 409
                                    ? 'Deze planning is vanaf een ander apparaat aangepast.'
                                    : e.message || 'Validatie fouten, zie de rood gemarkeerde velden',
                            confirm: 'Oke',
                        });

                        console.error(e);

                        return false;
                    }
                );
        };

        if (this.isNewRepetition()) {
            return save();
        }

        return this.confirmDialogService
            .confirm({
                title: 'Herhaling aanpassen',
                text: '',
                confirm: 'Opslaan',
                cancel: 'Annuleren',
                close: false,
                options: [
                    { name: 'Alleen deze planning aanpassen', value: RepeatOptions.OnlyThis },
                    { name: 'Deze en volgende herhalingen aanpassen', value: RepeatOptions.ThisAndFollowing },
                ],
                radio: true,
            })
            .then((option: { value: RepeatOptions } | { value: RepeatOptions }[]) => {
                const value = Array.isArray(option) ? option[0]?.value : option?.value;

                if (value === RepeatOptions.OnlyThis) {
                    return this.savePlanning(planning_template);
                }

                return save(planning?.planning_repetition_id);
            })
            .catch(() => {
                this.fieldFormData.form.markAsDirty();

                this.saving = false;

                return false;
            });
    }

    delete() {
        const { begindate } = this.fieldFormData.form?.value?.mainPlanningHas.phe;

        if (
            this.data.planning.planning_repetition_id &&
            hasFuturePlannings(this.existingPlannings, begindate, this.mainEntityType.key)
        ) {
            this.deleteRepetition();
            return;
        }

        this.confirmDialogService
            .confirm({
                title: 'Planning verwijderen',
                text: 'Weet u zeker dat u deze planning wilt verwijderen?',
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
            })
            .then(() => {
                this.planningService.deletePlanning(this.fieldFormData.orginal.id).then(() => {
                    this.close();
                });
            });
    }

    private deleteRepetition() {
        this.confirmDialogService
            .confirm({
                title: 'Planning verwijderen',
                text: '',
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
                close: false,
                options: [
                    { name: 'Alleen deze planning verwijderen', value: RepeatOptions.OnlyThis },
                    { name: 'Deze en volgende planningen verwijderen', value: RepeatOptions.ThisAndFollowing },
                ],
                radio: true,
            })
            .then((option: { value: RepeatOptions } | { value: RepeatOptions }[]) => {
                const value = Array.isArray(option) ? option[0]?.value : option?.value;

                this.planningService[value === RepeatOptions.OnlyThis ? 'deletePlanning' : 'deleteRepetitions'](
                    this.fieldFormData.orginal.id
                ).then(() => {
                    this.close();
                });
            });
    }

    private cancelRepetition(planning: Planning, nextPlanning: { id: number; begindate: string }) {
        return this.confirmDialogService
            .confirm({
                title: 'Herhaling verwijderen',
                text: 'Weet u zeker dat u de herhaling vanaf deze planning wilt verwijderen? Er zijn meerdere planningen die hier onder vallen.',
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
            })
            .then(
                () => {
                    return this.planningService
                        .save({
                            ...planning,
                            planning_repetition_id: undefined,
                        })
                        .toPromise()
                        .then(savedPlanning => {
                            return this.planningService.deleteRepetitions(nextPlanning.id).then(() => {
                                this.close();

                                return true;
                            });
                        });
                },
                () => {
                    this.saving = false;

                    return false;
                }
            );
    }

    copy() {
        if (this.fieldFormData.form.dirty) {
            this.confirmDialogService.confirm({
                title: 'Sla eerst je wijzigingen op',
                text: 'Voordat je een kopie kunt maken moet je eerst de wijzigingen die je hebt gemaakt opslaan.',
                confirm: 'Oke',
            });

            return;
        }

        this.subscriptions.add(
            this.entitiesService
                .getByType(LocalStorage.weekplanningTypeFilter ?? environment.planningEntityTypes.map(e => e.key))
                .subscribe(types => {
                    this.confirmDialogService
                        .confirm({
                            title: 'Kopiëer als',
                            text: '',
                            cancel: 'Annuleren',
                            close: false,
                            options: types.map(entity => {
                                return {
                                    name: `${entity.entitytypes[0].name} ${entity.entitytypes[0].key === this.mainEntityType.key ? '(huidig)' : ''}`,
                                    bgColor: '',
                                    icon: `fal ${entity.entitytypes[0].icon}`,
                                    entity,
                                };
                            }),
                        })
                        .then((data: { entity?: any } | { entity?: any }[]) => {
                            const entity = Array.isArray(data) ? data[0]?.entity : data?.entity;

                            this.dialog.open(TracklinePlanningEditDialogComponent, {
                                disableClose: true,
                                data: {
                                    copy: true,
                                    planning: this.entityTypeService.getPlanningObject(this.fieldFormData),
                                    entityTypeKey: entity ? entity.entitytypes[0].key : null,
                                },
                                panelClass: 'planning-edit-dialog',
                            });

                            this.close();
                        });
                })
        );
    }

    private saveWhenDirty(cb: () => void) {
        if (this.fieldFormData.form.dirty === false) {
            cb();
            this.close();
            return;
        }

        this.save().then(result => {
            if (result === true) {
                cb();
            }
        });
    }

    dayReport() {
        this.saveWhenDirty(() => {
            this.router.navigate([`/dagrapport/${this.fieldFormData?.orginal?.id}`]);
        });
    }

    orderForm() {
        this.saveWhenDirty(() => {
            window.open('/opdrachtformulier/' + this.fieldFormData?.orginal?.id + '?print=true');
        });
    }

    close(planning: Readonly<Planning> = null) {
        if (planning?.id) {
            this.planningHighlightService.triggerSavedPlanning(planning.id);
        }

        this.dialogRef.close(planning);
    }

    ngOnDestroy() {
        this.timers.forEach(t => clearTimeout(t));
        super.ngOnDestroy();
    }
}

function lastIndexOfPlanningWithDate(plannings: { id: number; begindate: string }[], begindate: Date) {
    return plannings.findLastIndex(p => Utils.matchesDate(new Date(p.begindate), begindate));
}

function remainingPlanningsInRepetition(plannings: { id: number; begindate: string }[], begindate: Date) {
    return plannings.length - lastIndexOfPlanningWithDate(plannings, begindate);
}

function isLastPlanningInRepetition(plannings: { id: number; begindate: string }[], begindate: Date) {
    if (plannings.length === 0) {
        return false;
    }

    return lastIndexOfPlanningWithDate(plannings, begindate) === plannings.length - 1;
}

function hasFuturePlannings(
    plannings: { id: number; begindate: string; entitytype_key: string }[],
    begindate: Date,
    entityType: string
) {
    if (plannings.length < 2) return false;

    const currentBeginTime = begindate ? new Date(begindate).getTime() : NaN;

    return plannings.some(p => {
        const planningBegindate = p.begindate;

        if (!planningBegindate || Number.isNaN(currentBeginTime) || p.entitytype_key !== entityType) return false;

        return new Date(planningBegindate).getTime() > currentBeginTime;
    });
}

function hasUpdatedAt(a: Readonly<{ updated_at?: Date }>, b: Readonly<{ updated_at?: Date }>): boolean {
    return new Date(a.updated_at).getTime() !== new Date(b.updated_at).getTime();
}

function hasListChanges<T extends { id?: number; updated_at?: Date }>(
    incomingList: T[] | null | undefined,
    originalList: T[] | null | undefined
) {
    return (incomingList ?? []).some(item => {
        const originalItem = (originalList ?? []).find(o => o.id === item.id);
        return !originalItem || hasUpdatedAt(item, originalItem);
    });
}

function hasExternalChanges(incoming: Readonly<Planning>, original: Readonly<Planning>): boolean {
    if (!original) {
        return false;
    }

    const changeInPlanningHas = hasListChanges(incoming.planning_has, original.planning_has);
    const changeInUserPlanning = hasListChanges(incoming.user_planning, original.user_planning);
    const changeInMaterials = hasListChanges(incoming.planning_materials, original.planning_materials);

    return hasUpdatedAt(incoming, original) || changeInPlanningHas || changeInUserPlanning || changeInMaterials;
}

function calculateNrOfRepetitionsFromEndDate(
    repetitionEndDateValue: Date,
    begindateValue: Date,
    interval_weekValue: number,
    weekdaysValue: string[]
) {
    if (
        !begindateValue ||
        !repetitionEndDateValue ||
        !interval_weekValue ||
        !Array.isArray(weekdaysValue) ||
        weekdaysValue.length === 0
    ) {
        return 0;
    }

    const fromDate = new Date(begindateValue);
    const toDate = new Date(repetitionEndDateValue);

    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    if (fromDate.getTime() > toDate.getTime()) {
        return 0;
    }

    let count = 0;
    const loopDate = new Date(fromDate);
    const firstWeek = +formatDate(fromDate, 'w', 'nl');

    while (loopDate.getTime() <= toDate.getTime()) {
        const week = +formatDate(loopDate, 'w', 'nl');
        const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();

        const isCorrectWeek = (week - firstWeek) % interval_weekValue === 0;
        const isSelectedDay = weekdaysValue.includes(`repeat_${weekDayName}`);

        if (isCorrectWeek && isSelectedDay) {
            count++;
        }

        loopDate.setDate(loopDate.getDate() + 1);
    }

    return count;
}

function generateDates(
    begindate: Readonly<Date>,
    nrOfRepetitions: Readonly<number>,
    interval_week: Readonly<number>,
    weekdays: ReadonlyArray<string>
) {
    const resultDates: Date[] = [];

    if (!Array.isArray(weekdays) || weekdays.length === 0) return resultDates;

    if (!begindate || !interval_week || !nrOfRepetitions)
        throw new Error('Invalid parameters provided for date generation');

    let loopDate = new Date(begindate);
    let count = 0;

    const maxDate = new Date(begindate);

    maxDate.setFullYear(maxDate.getFullYear() + 5);

    const maxTime = maxDate.getTime();

    const firstWeek = +formatDate(loopDate, 'w', 'nl');

    while (count < nrOfRepetitions && loopDate.getTime() <= maxTime) {
        const week = +formatDate(loopDate, 'w', 'nl');
        const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();

        const isCorrectWeek = (week - firstWeek) % interval_week === 0;
        const isSelectedDay = weekdays.includes(`repeat_${weekDayName}`);

        if (isCorrectWeek && isSelectedDay) {
            resultDates.push(new Date(loopDate));
            count++;
        }

        loopDate.setDate(loopDate.getDate() + 1);
    }

    return resultDates;
}
